import * as angular from 'angular';
import {IController, IPromise, IQService} from 'angular';
import * as _ from 'lodash';
import {BatchRequestsService} from '../../../batch/services/batch-requests.service';
import {BatchDialogService} from '../../../batch/services/batch-dialogs-service.service';
import {BringozLoaderService} from '../../../commons/services/bringoz-loader.service';
import {ConfirmDialogService} from '../../../commons/services/confirm-dialog.service';
import {ConfirmChangeStateService} from '../../../commons/services/confirm-change-state.service';
import {AgentAccountAssignmentDialogService} from '../../../account/services/agent-account-assignment-dialog.service';
import {LogisticsVehiclesAlertsService} from '../../services/logistics-vehicles-alerts.service';
import {
  LOGISTICS_VEHICLES_IMPORT_CONFIG,
  LogisticsVehiclesImportColumn
} from '../../constants/logistics-vehicles-import.const';

/**
 * Row as returned by /{importerPath}/validate - ImportVerification<LogisticsVehicle>. `entity` holds
 * the file values and is null for rows the server will not import (duplicate / foreign External ID).
 * `rowId`, `errorsDisplay`, `hasErrors` and `cells` are view-only and stripped before import.
 */
export interface LogisticsVehiclesImportRow {
  rowNumber?: number;
  entity?: {[field: string]: any} | null;
  mappingErrors?: Array<LogisticsVehiclesImportMappingError | string>;
  rowId?: string;
  errorsDisplay?: string;
  hasErrors?: boolean;
  cells?: string[];
}

export interface LogisticsVehiclesImportMappingError {
  type?: string;
  description?: string;
  message?: string;
  fieldName?: string;
}

/**
 * Logistic vehicles file import: upload a file (optionally through a saved mapping schema), review
 * the server validation, and import once no row has errors. Validate does not tell create from
 * update - /import updates a vehicle matched by id or External ID and creates the others.
 */
export class LogisticsVehiclesImportViewCtrl implements IController {

  public importConfig = LOGISTICS_VEHICLES_IMPORT_CONFIG;
  public schemaPermissions: string[] = [LOGISTICS_VEHICLES_IMPORT_CONFIG.schemaPermission];
  public rows: LogisticsVehiclesImportRow[] = [];
  public visibleRows: LogisticsVehiclesImportRow[] = [];
  public totals: {total: number, errors: number} = {total: 0, errors: 0};
  public currentNavItem: string = 'total';
  public uploadComplete: boolean = false;
  public isFieldMappingOpen: boolean = false;
  public schemas: any[] = [];
  public schemaDefaultFields: any[] = [];

  private schemasRequest: IPromise<void>;

  /* @ngInject */
  constructor(
    private $state,
    private $log,
    private $q: IQService,
    private UUIDService,
    private IAMPermissions,
    private agentAccountAssignmentDialogService: AgentAccountAssignmentDialogService,
    private batchRequestsService: BatchRequestsService,
    private batchDialogService: BatchDialogService,
    private bringozLoaderService: BringozLoaderService,
    private confirmDialogService: ConfirmDialogService,
    private confirmChangeStateService: ConfirmChangeStateService,
    private logisticsVehiclesAlertsService: LogisticsVehiclesAlertsService
  ) {
  }

  $onInit() {
    this.schemasRequest = this._loadSchemas();
  }

  $onDestroy() {
    // The unsaved changes guard is global, leaving it enabled would prompt on unrelated screens.
    this.confirmChangeStateService.reset();
  }

  changeNavItem(item: string) {
    this.currentNavItem = item;
    this._applyRows(this.rows);
  }

  onUpload(event: Event) {
    // Wait for the schemas, else a quick click opens the dialog with an empty schema list.
    this.schemasRequest
      .then(() => this.agentAccountAssignmentDialogService
        .showUploadAgentAssignmentsDialog(event, this.importConfig, this.schemas))
      .then((rows) => {
        this.currentNavItem = 'total';
        this._applyRows(_.map(rows || [], (row) => this._enrichRow(row)));
        this.confirmChangeStateService.setIsEnabled(this.uploadComplete);
      }, () => {
      });
  }

  onRemoveRow(row: LogisticsVehiclesImportRow) {
    this._applyRows(_.reject(this.rows, (r) => r.rowId === row.rowId));

    if (!this.rows.length) {
      this.currentNavItem = 'total';
      this.confirmChangeStateService.reset();
    }
  }

  onCancel(event: Event) {
    return this.confirmDialogService
      .confirm('Cancel Upload', 'Are you sure you want to cancel the upload?', event)
      .then(() => {
        this._reset();
      })
      .catch(() => {
        this.$log.info('Dialog Closed');
      });
  }

  onImport(event: Event) {
    // The importer expects a clean file: rows with errors other than duplicates keep their entity
    // and would be imported, so the button stays disabled until every error row is fixed or removed.
    if (!this.rows.length || this.totals.errors) {
      return;
    }

    const items = _.map(this.rows, (row) => _.omit(row, ['rowId', 'errorsDisplay', 'hasErrors', 'cells']));

    this.bringozLoaderService.on();

    this.batchRequestsService.createAgentAssignmentsBatch(this.importConfig.importerPath, items)
      .then((batch) => {
        this.bringozLoaderService.off();
        // /status returns the v2 batch details ({batch, totals}), counting tasks of 50 rows - the
        // dialog shows the rows sent instead.
        return this.batchDialogService.batchV2ImportProgress(
          event, batch, this.importConfig.importerPath, this.importConfig.label, items.length
        );
      })
      .then((errorItems: any) => {
        this._applyBatchErrors(errorItems || []);
      })
      .catch((error) => {
        if (error) {
          this.logisticsVehiclesAlertsService.importError(error);
          this.$log.error('Failed to import logistic vehicles', error);
        } else {
          this.$log.info('Dialog Closed');
        }
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });
  }

  onBackToVehicles() {
    this.confirmChangeStateService.reset();
    this.$state.go('logistics-vehicles-list');
  }

  onOpenFieldMappingPanel() {
    this.isFieldMappingOpen = true;
  }

  onCloseFieldMappingPanel() {
    this.isFieldMappingOpen = false;
  }

  onSchemaCreated(schema) {
    this.schemas = [...this.schemas, schema];
  }

  onSchemaUpdated(schema) {
    this.schemas = _.map(this.schemas, (s) => s.id === schema.id ? schema : s);
  }

  onSchemaDeleted(schemaId) {
    this.schemas = _.reject(this.schemas, (s) => s.id === schemaId);
  }

  // ******************************
  //        Private methods
  // ******************************

  private _loadSchemas(): IPromise<void> {
    // The schema endpoints answer 403 without import.schemas, and the global interceptor turns any
    // 403 into the 403 page - so users who may only import vehicles must not call them.
    if (!this.IAMPermissions.hasPermission(this.importConfig.schemaPermission)) {
      return this.$q.when();
    }

    // batchRequestsService returns native promises - $q.all adopts them so the results land inside
    // a digest. A failure must not block uploading, it only means no saved mapping can be picked.
    return this.$q.all([
      this.$q.when(this.batchRequestsService.getSchemas(this.importConfig.entity)).catch(() => []),
      this.$q.when(this.batchRequestsService.getSchemaDefaultFields(this.importConfig.entity)).catch(() => [])
    ])
      .then(([schemas, schemaDefaultFields]: any[]) => {
        this.schemas = schemas || [];
        this.schemaDefaultFields = schemaDefaultFields || [];
      });
  }

  private _applyRows(rows: LogisticsVehiclesImportRow[]) {
    this.rows = rows;
    this.totals = this._getTotals(rows);
    this.uploadComplete = !!rows.length;
    this.visibleRows = this.currentNavItem === 'errors'
      ? _.filter(rows, (row) => row.hasErrors)
      : rows;
  }

  private _enrichRow(row: LogisticsVehiclesImportRow): LogisticsVehiclesImportRow {
    const mappingErrors = row.mappingErrors || [];

    return {
      ...row,
      rowId: this.UUIDService.generate(),
      hasErrors: _.some(mappingErrors, (error) => this._isBlockingError(error)),
      errorsDisplay: this._formatErrors(mappingErrors),
      cells: _.map(this.importConfig.columns, (column) => this._formatCell(row, column))
    };
  }

  private _formatCell(row: LogisticsVehiclesImportRow, column: LogisticsVehiclesImportColumn): string {
    const value = _.get(row.entity, column.field);

    if (_.isNil(value) || value === '' || (_.isArray(value) && !value.length)) {
      return '-';
    }

    if (column.format === 'metersToKm') {
      return String(_.round(value / 1000, 2));
    }

    return _.isArray(value) ? value.join(', ') : String(value);
  }

  private _formatErrors(mappingErrors: Array<LogisticsVehiclesImportMappingError | string>): string {
    const messages = _.chain(mappingErrors)
      .map((error) => _.isString(error) ? error : (error?.description || error?.message || error?.fieldName))
      .compact()
      .uniq()
      .value();

    return messages.length ? messages.join(', ') : '-';
  }

  private _isBlockingError(error: LogisticsVehiclesImportMappingError | string): boolean {
    return _.isString(error) || !error.type || error.type === 'Error';
  }

  private _getTotals(rows: LogisticsVehiclesImportRow[]) {
    return {
      total: rows.length,
      errors: _.filter(rows, (row) => row.hasErrors).length
    };
  }

  private _applyBatchErrors(errorItems: any[]) {
    if (!errorItems.length) {
      this.logisticsVehiclesAlertsService.importFinished();
      this._reset();
      this.onBackToVehicles();
      return;
    }

    const errorRowNumbers = _.map(errorItems, 'rowNumber');

    const failedRows = _.chain(this.rows)
      .filter((row) => _.includes(errorRowNumbers, row.rowNumber))
      .map((row) => {
        const errorItem = _.find(errorItems, (item) => item.rowNumber === row.rowNumber);
        return this._enrichRow({
          ...row,
          mappingErrors: _.get(errorItem, 'mappingErrors', [])
        });
      })
      .value();

    // If the importer does not report row numbers the failures cannot be matched back to rows -
    // keep the current rows on screen instead of showing an empty errors tab.
    if (!failedRows.length) {
      this.logisticsVehiclesAlertsService.importError();
      this.$log.error('Import reported failures that could not be matched to uploaded rows', errorItems);
      return;
    }

    this.currentNavItem = 'errors';
    this._applyRows(failedRows);
  }

  private _reset() {
    this.currentNavItem = 'total';
    this._applyRows([]);
    this.confirmChangeStateService.reset();
  }
}

angular
  .module('bringoz.admin.logistics-vehicle')
  .component('logisticsVehiclesImportView', {
    bindings: {},
    template: require('./logistics-vehicles-import-view.html').default,
    controller: LogisticsVehiclesImportViewCtrl
  });
